import { useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useInstallStateForGame } from '../hooks/useInstallStateForGame';

/**
 * Issue #447 — `<InstallStateChip>` surfaces a silent scaffold-install
 * failure as a loud, durable UI signal. Rendered on the Dashboard
 * `<GameCard>` and in the Workspace header so neither surface can
 * silently send the user down the "Cannot find module" path.
 *
 * Mirrors the deploy-failure chip pattern from #408: amber tone, click
 * to act, ARIA-friendly. Distinct from `<DeployFailureCard>` (a full
 * card) because the install state is a binary "good/bad" gate with
 * one retry CTA — no failed-job enumeration to render.
 *
 * Query strategy: short staleTime + refetch on mount keeps the chip
 * in sync after a retry without the renderer maintaining its own
 * subscription. Following the `useTemplateUpdateCheck` precedent
 * already used by `<TemplateUpdateBadge>` (sibling chip on the same
 * card).
 */
export function InstallStateChip({
  gameId,
  variant = 'pill',
  enabled,
}: {
  gameId: string;
  /**
   * `'pill'` is the compact form rendered on the dashboard `<GameCard>`
   * (alongside `<TemplateUpdateBadge>`). `'banner'` is the wider form
   * for the Workspace header — same colour and message, more
   * horizontal room for the retry copy.
   */
  variant?: 'pill' | 'banner' | 'icon';
  /**
   * Dashboard lazy-load path (`docs/dashboard-lazy-load-plan.md`
   * lines 109-140): set to `false` while the card is below the fold
   * so we don't fire `install-state:get` for off-screen cards. Defaults
   * to `true` for non-Dashboard call sites (Workspace banner).
   */
  enabled?: boolean;
}): React.JSX.Element | null {
  const externalEnabled = enabled ?? true;
  const installState = useInstallStateForGame(gameId, {
    enabled: externalEnabled,
  });

  const handleRetry = useCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (installState.isRetrying) return;
      await installState.retryInstall();
    },
    [installState],
  );

  // The chip is silent on every status other than `'failed'` — null
  // state file (most games), `'ok'`, network errors during the read,
  // they all collapse to no chip. The agent-dispatch sanity check in
  // `agent-adapter.sendMessage` is the belt-and-braces gate; this
  // chip is the visible cue that the gate WILL fire.
  if (!installState.data || installState.data.status !== 'failed') return null;

  const reason = installState.retryError ?? installState.data.reason ?? 'install failed';
  const tooltip = installState.isRetrying
    ? `Retrying install for ${gameId}...`
    : `Dependencies not installed (${reason}). Click to retry.`;

  if (variant === 'banner') {
    // Codex Round-2 fold (#457): restored to `role="alert"` after the
    // round-1 downgrade to `role="status"` + `aria-live="polite"` was
    // judged too aggressive. "Dependencies not installed" IS an
    // actionable warning the user needs to hear on first mount — a
    // polite live region under-announces, especially when the region
    // is pre-populated at render time (screen readers often skip the
    // initial value of a polite region entirely).
    //
    // The Round-1 concern was the Workspace also mounts
    // `<DeployFailureCard>` with `role="alert"`, and two simultaneous
    // alerts in theory step on each other. In practice: (a) they
    // co-occur very rarely — install-failed state is set at scaffold
    // time and clears on retry, deploy-failed state is set when a
    // publish run fails; the user has to be in the "just retried the
    // deploy on a game with a broken scaffold" niche to hit both, and
    // (b) screen readers QUEUE alert regions rather than truncating —
    // VoiceOver / NVDA / JAWS all announce them sequentially. So we
    // accept the rare dual-alert as an acceptable trade-off vs. the
    // common case of a silent under-announce. This was the simpler-
    // option call-out in the Round-2 Codex finding.
    return (
      <div
        data-testid="install-state-banner"
        role="alert"
        className="mx-6 mt-3 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm"
      >
        <div className="flex items-center gap-2 text-amber-200">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8 1.5L14.5 13.5H1.5L8 1.5Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <path d="M8 6v3.5M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span data-testid="install-state-banner-message">
            Dependencies not installed{' '}
            <span className="font-mono text-xs text-amber-300/80">({reason})</span>
          </span>
        </div>
        <button
          type="button"
          data-testid="install-state-banner-retry"
          onClick={handleRetry}
          disabled={installState.isRetrying}
          className="rounded border border-amber-500/40 bg-amber-500/20 px-3 py-1 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-500/30 disabled:opacity-50"
          aria-label="Retry installing dependencies"
        >
          {installState.isRetrying ? 'Installing…' : 'Retry install'}
        </button>
      </div>
    );
  }

  // Icon variant — small circle matching UpdateIconButton's style.
  // Used in the LazyGameCard footer row so the signal doesn't overlay
  // the hero image.
  if (variant === 'icon') {
    return (
      <div
        role="button"
        tabIndex={0}
        data-testid="install-state-chip-icon"
        onClick={handleRetry}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            handleRetry(e as unknown as React.MouseEvent);
          }
        }}
        title={tooltip}
        aria-label={tooltip}
        className="shrink-0 cursor-pointer rounded-full bg-amber-500/20 p-1.5 text-amber-300 transition-colors hover:bg-amber-500/30"
      >
        <AlertTriangle size={12} strokeWidth={2.5} />
      </div>
    );
  }

  // Codex YELLOW fold (#457): pill is a `<button>`, not a `<div
  // role="button">`. A semantic button:
  //   - is focusable by default (no `tabIndex={0}` needed),
  //   - fires `onClick` on both Enter and Space for free (no explicit
  //     `onKeyDown` branching — which is exactly what the previous
  //     hand-rolled handler did, and which had no test coverage
  //     because SSR can't fire events),
  //   - announces as "button" to screen readers without the explicit
  //     `role="button"` attribute,
  //   - participates in form/keyboard navigation correctly.
  // The previous `<div role="button">` form was the textbook ARIA
  // antipattern: re-implementing what the platform already gives you.
  // Tests that previously asserted `role="button"` + `tabindex="0"`
  // are updated to assert the `<button>` tag instead.
  return (
    <button
      type="button"
      data-testid="install-state-chip"
      onClick={handleRetry}
      disabled={installState.isRetrying}
      title={tooltip}
      aria-label={tooltip}
      className="shrink-0 cursor-pointer rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium text-amber-300 transition-colors hover:bg-amber-500/30 disabled:opacity-50"
    >
      {installState.isRetrying ? 'Installing…' : 'Deps not installed'}
    </button>
  );
}
